// AboutPage.js
import React from 'react';
import Header from '../components/Header';
import ContactFooter from '../components/ContactFooter';
import styles from './AboutPage.module.css';

// Data constants
const journeyData = [
	{
		period: "2024 - Present",
		title: "AI Research & Development",
		description: "Multimodal AI systems, focusing on the intersection of computer vision, natural language processing, and hardware acceleration. Building production-ready AI systems for real-world applications.",
		technologies: ["Novel AI Architecture", "CUDA Optimization", "Embeded System inference", "Model Quantization"]
	},
	{
		period: "2021 - 2023",
		title: "Physics to AI Transition",
		description: "Leveraged deep physics background and scientific computing to tackle complex AI problems, particularly in areas requiring mathematical rigor and systems thinking.",
		technologies: ["Deep Learning", "Computer Vision", "Large Language Models", "Scientific Computing"]
	},
	{
		period: "2018 - 2021",
		title: "High-Precision Optical Systems",
		description: "Designed and implemented interferometers, spectrometers, and other high precision optical systems, developing expertise in hardware-software integration and real-time control systems. Scientific computing and data analysis.",
		technologies: ["Optical Design", "Real-time Control", "Embedded Systems", "Signal Processing", "Scientific Computing"]
	},
];

// const competencies = [
// 	{
// 		category: "AI & Machine Learning",
// 		skills: [
// 			"Deep Learning Architecture Design",
// 			"Computer Vision & Image Processing",
// 			"Natural Language Processing",
// 			"Reinforcement Learning",
// 			"Neural Network Optimization",
// 			"Model Deployment & Scaling"
// 		]
// 	},
// 	{
// 		category: "Systems & Hardware",
// 		skills: [
// 			"CUDA & GPU Programming",
// 			"Embedded Systems Development",
// 			"Real-time Control Systems",
// 			"Hardware-Software Integration",
// 			"Linux Kernel Development",
// 			"High-Performance Computing"
// 		]
// 	},
// 	{
// 		category: "Software Engineering",
// 		skills: [
// 			"Full-Stack Web Development",
// 			"API Design & Implementation",
// 			"Database Architecture",
// 			"Cloud Infrastructure",
// 			"DevOps & CI/CD",
// 			"Software Architecture"
// 		]
// 	},
// 	{
// 		category: "Research & Analysis",
// 		skills: [
// 			"Scientific Method & Experimentation",
// 			"Statistical Analysis & Modeling",
// 			"Technical Writing & Documentation",
// 			"Patent Application & IP Strategy",
// 			"Peer Review & Collaboration",
// 			"Grant Writing & Funding"
// 		]
// 	}
// ];

const philosophyData = [
	{
		title: "Interdisciplinary Innovation",
		description: "I believe the most breakthrough innovations occur at the intersection of disciplines. My physics background provides unique insights into AI architecture design, optimization strategies, and system behavior understanding."
	},
	{
		title: "First Principles Thinking",
		description: "Rooted in scientific methodology, I approach problems by breaking them down to fundamental principles, enabling novel solutions and avoiding conventional limitations that constrain purely software-focused approaches."
	},
	{
		title: "End-to-End Systems",
		description: "From mathematical foundations to hardware implementation, I design systems holistically. This comprehensive view enables optimization opportunities that are invisible when focusing on individual components."
	},
	{
		title: "Open Science & Collaboration",
		description: "Progress accelerates through open research and collaborative development. I contribute to the scientific community through publications, open-source projects, and knowledge sharing initiatives."
	}
];

const focusAreas = [
	{
		title: "Multimodal AI Architecture",
		description: "Developing next-generation architectures that seamlessly integrate visual, textual, and sensory data streams for more human-like AI understanding."
	},
	{
		title: "Physics-Informed Machine Learning",
		description: "Incorporating physical laws and constraints into neural networks for improved generalization and interpretability in scientific applications."
	},
	{
		title: "Hardware-Accelerated AI",
		description: "Optimizing AI algorithms for specialized hardware, including custom silicon and novel computing paradigms like optical and quantum processors."
	}
];

export default function AboutPage() {
	const pageSubtitle = "Professional Journey | From Physics to Artificial Intelligence";
	const bannerImageUrl = "/images/BannerPage1.jpg";

	return (
		<div className={styles.pageContainer}>
			<Header backgroundImageUrl={bannerImageUrl} subtitle={pageSubtitle} />

			<main className={styles.mainContent}>
				{/* Introduction Section */}
				<section className={styles.introSection}>
					<div className={styles.introContent}>
						<h1 className={styles.pageTitle}>About Me</h1>

						{/* Profile Section */}
						<div className={styles.profileSection}>
							<div className={styles.profileImageContainer}>
								<div
									className={styles.profileImage}
									style={{
										backgroundImage: "url(/images/profile_picture_round.jpg)",
										backgroundSize: 'cover',
										backgroundPosition: 'center',
										backgroundRepeat: 'no-repeat'
									}}
								></div>
							</div>
							<div className={styles.profileInfo}>
								<h2 className={styles.profileName}>Gesit Tali Singgih</h2>
								{/* <div className={styles.profileTitle}>
									Multidisciplinary AI Engineer & Researcher
								</div> */}
								<div className={styles.socialLinks}>
									<a
										href="https://x.com/entropicEm"
										target="_blank"
										rel="noopener noreferrer"
										className={styles.socialLink}
										aria-label="Follow on X (Twitter)"
									>
										<svg 
											className={styles.socialIcon}
											viewBox="0 0 24 24" 
											width="18" 
											height="18" 
											fill="currentColor"
											aria-hidden="true"
										>
											<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
										</svg>
										<span className={styles.socialText}>@entropicEm</span>
									</a>
								</div>
							</div>
						</div>

						<div className={styles.introText}>
							<p className={styles.leadParagraph}>
								I am a multidisciplinary AI engineer and researcher who bridges the gap between
								theoretical physics and practical artificial intelligence implementations.
							</p>
							<p className={styles.bodyParagraph}>
								My unique journey from high-precision optical systems to cutting-edge AI research
								has equipped me with a rare combination of deep technical knowledge, rigorous
								scientific methodology, and practical engineering experience. This background
								enables me to approach complex problems with both theoretical depth and
								implementation expertise.
							</p>
							<p className={styles.bodyParagraph}>
								Currently, I focus on state-of-the-art multimodal AI systems,
								with particular emphasis on hardware-software co-design to optimize compute to it's limit.
								My work spans from foundational research to
								production-ready implementations.
							</p>
						</div>
					</div>
				</section>

				{/* Professional Journey Timeline */}
				<section className={styles.journeySection}>
					<h2 className={styles.sectionTitle}>Professional Journey</h2>
					<div className={styles.timeline}>
						{journeyData.map((item, index) => (
							<div key={index} className={styles.timelineItem}>
								<div className={styles.timelineMarker}></div>
								<div className={styles.timelineContent}>
									<div className={styles.timelinePeriod}>{item.period}</div>
									<h3 className={styles.timelineTitle}>{item.title}</h3>
									<p className={styles.timelineDescription}>{item.description}</p>
									<div className={styles.timelineTechnologies}>
										{item.technologies.map((tech, techIndex) => (
											<span key={techIndex} className={styles.technologyTag}>
												{tech}
											</span>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* Core Competencies */}
				{/* <section className={styles.competenciesSection}>
					<h2 className={styles.sectionTitle}>Core Competencies</h2>
					<div className={styles.competenciesGrid}>
						{competencies.map((category, index) => (
							<div key={index} className={styles.competencyCategory}>
								<h3 className={styles.competencyTitle}>{category.category}</h3>
								<ul className={styles.skillsList}>
									{category.skills.map((skill, skillIndex) => (
										<li key={skillIndex} className={styles.skillItem}>
											<span className={styles.skillBullet}>▶</span>
											{skill}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</section> */}

				{/* Philosophy & Approach */}
				<section className={styles.philosophySection}>
					<h2 className={styles.sectionTitle}>Philosophy & Approach</h2>
					<div className={styles.philosophyContent}>
						<div className={styles.philosophyGrid}>
							{philosophyData.map((item, index) => (
								<div key={index} className={styles.philosophyCard}>
									<h3 className={styles.philosophyCardTitle}>{item.title}</h3>
									<p className={styles.philosophyCardDescription}>{item.description}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Current Focus */}
				{/* <section className={styles.currentFocusSection}>
					<h2 className={styles.sectionTitle}>Current Research Focus</h2>
					<div className={styles.currentFocusContent}>
						<div className={styles.focusAreas}>
							{focusAreas.map((area, index) => (
								<div key={index} className={styles.focusArea}>
									<h4 className={styles.focusAreaTitle}>{area.title}</h4>
									<p className={styles.focusAreaDescription}>{area.description}</p>
								</div>
							))}
						</div>
					</div>
				</section> */}
			</main>

			<ContactFooter
				title="Let's Collaborate"
				description="Whether you're interested in research collaboration, consulting opportunities, or technical discussions, I'd love to hear from you."
				copyrightText="© 2025 Entrosystem. All rights reserved."
				backgroundImageUrl={bannerImageUrl}
			/>
		</div>
	);
}